/**
 * Backend-Powered TradingView Datafeed
 * 
 * Fetches historical candles and real-time prices from our backend API,
 * which in turn uses the configured market data provider (CoinAPI or Massive).
 * 
 * This replaces the direct Binance datafeed with a provider-agnostic solution.
 */

import { wsClient, type WsPriceMessage, type WsMessage } from "@/backend/ws-client";
import { MARKETS } from "@/solana/constants";

import { API_BASE_URL as API_URL } from "@/backend/config";
const MARKETS_CACHE_KEY = "nordex_markets_cache";

/**
 * Get market precision from localStorage cache or fallback constants.
 * Used to configure TradingView chart pricescale dynamically per market.
 */
function getMarketPrecision(symbol: string): { quotePrecision: number; basePrecision: number } {
  // 1. Try localStorage cache (source of truth from backend)
  try {
    const cached = localStorage.getItem(MARKETS_CACHE_KEY);
    if (cached) {
      const { data } = JSON.parse(cached);
      const market = data?.find((m: { symbol: string }) => m.symbol === symbol);
      if (market && typeof market.quotePrecision === "number") {
        return {
          quotePrecision: market.quotePrecision,
          basePrecision: market.basePrecision ?? 4,
        };
      }
    }
  } catch (e) {
    console.warn("[BackendDatafeed] Failed to read markets cache:", e);
  }

  // 2. Fallback to hardcoded constants
  const hardcoded = MARKETS[symbol as keyof typeof MARKETS];
  if (hardcoded) {
    return {
      quotePrecision: hardcoded.quotePrecision,
      basePrecision: hardcoded.basePrecision,
    };
  }

  // 3. Default fallback
  return { quotePrecision: 2, basePrecision: 4 };
}

// Map TradingView resolution to our backend format
const resolutionMap: Record<string, string> = {
  "1": "1",
  "5": "5",
  "15": "15",
  "60": "60",
  "240": "240",
  "1D": "1D",
  "D": "1D",
  "1W": "1W",
  "W": "1W",
  "1M": "1M",
  "M": "1M",
};

// Normalize resolution to a canonical form for consistent Map keys
// TradingView can pass "1W" or "W", "1D" or "D", "1M" or "M" - we need consistent keys
const normalizeResolution = (resolution: string): string => {
  const mapped = resolutionMap[resolution];
  return mapped || resolution;
};

type SubscriberData = {
  callback: TradingView.SubscribeBarsCallback;
  resolution: string;
  lastBar: TradingView.Bar | null;
};

export class BackendDatafeed implements TradingView.IBasicDataFeed {
  private subscribers: Map<string, SubscriberData> = new Map();
  private symbolSubscriptions: Map<string, Set<string>> = new Map(); // symbol -> Set<listenerGuid>
  // WebSocket unsubscribe functions keyed by symbol (not per-subscriber)
  // This ensures we can always clean up the WS connection regardless of which subscriber is removed last
  private wsUnsubscribers: Map<string, () => void> = new Map();
  // Store the last historical bar per symbol+resolution for continuity with real-time updates
  private lastHistoricalBars: Map<string, TradingView.Bar> = new Map();
  // Track which symbol+resolution pairs have had historical data loaded
  // This prevents race conditions where real-time updates arrive before historical data
  private historicalDataLoaded: Set<string> = new Set();

  onReady(callback: TradingView.OnReadyCallback): void {
    setTimeout(() => {
      callback({
        supported_resolutions: ["1", "5", "15", "60", "240", "1D", "1W", "1M"] as TradingView.ResolutionString[],
        supports_marks: false,
        supports_timescale_marks: false,
        supports_time: true,
      });
    }, 0);
  }

  searchSymbols(
    _userInput: string,
    _exchange: string,
    _symbolType: string,
    onResult: TradingView.SearchSymbolsCallback
  ): void {
    // Not implementing symbol search for now
    onResult([]);
  }

  resolveSymbol(
    symbolName: string,
    onResolve: TradingView.ResolveCallback
  ): void {
    // Clean symbol format (remove exchange prefix if present)
    const symbol = symbolName.includes(":") ? symbolName.split(":")[1] : symbolName;

    // Get dynamic precision from market config (database → localStorage cache → fallback)
    const { quotePrecision } = getMarketPrecision(symbol);
    // TradingView pricescale = 10^quotePrecision (e.g., precision 1 → scale 10, precision 2 → scale 100)
    const pricescale = Math.pow(10, quotePrecision);

    const symbolInfo: TradingView.LibrarySymbolInfo = {
      name: symbol,
      description: symbol,
      type: "crypto",
      session: "24x7",
      exchange: "NORDEX",
      listed_exchange: "NORDEX",
      timezone: "Etc/UTC" as TradingView.Timezone,
      format: "price",
      pricescale,
      minmov: 1,
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      supported_resolutions: ["1", "5", "15", "60", "240", "1D", "1W", "1M"] as TradingView.ResolutionString[],
      data_status: "streaming",
    };

    setTimeout(() => onResolve(symbolInfo), 0);
  }

  getBars(
    symbolInfo: TradingView.LibrarySymbolInfo,
    resolution: TradingView.ResolutionString,
    periodParams: TradingView.PeriodParams,
    onResult: TradingView.HistoryCallback
  ): void {
    const symbol = symbolInfo.name;
    const mappedResolution = resolutionMap[resolution] || "1D";
    const from = periodParams.from;
    const to = periodParams.to;

    const url = `${API_URL}/api/tradingview/history?symbol=${symbol}&resolution=${mappedResolution}&from=${from}&to=${to}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data: { bars: Array<{ time: number; open: number; high: number; low: number; close: number; volume: number }>; noData: boolean }) => {
        if (data.noData || !data.bars || data.bars.length === 0) {
          onResult([], { noData: true });
          return;
        }

        const bars: TradingView.Bar[] = data.bars.map((bar) => ({
          time: bar.time,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume,
        }));

        // Use normalized resolution for consistent key matching
        const normalizedRes = normalizeResolution(resolution);
        const key = `${symbol}_${normalizedRes}`;
        const lastBar = bars.length > 0 ? bars[bars.length - 1] : null;
        
        if (lastBar) {
          // Only store if this bar is MORE RECENT than what's already stored
          // TradingView calls getBars multiple times - first for recent data, then for older history
          // We must keep the MOST RECENT bar, not the bar from the most recent fetch
          const existingBar = this.lastHistoricalBars.get(key);
          if (!existingBar || lastBar.time > existingBar.time) {
            this.lastHistoricalBars.set(key, lastBar);
          }
        }
        
        // Mark this symbol+resolution as having historical data loaded
        this.historicalDataLoaded.add(key);
        
        // Update existing subscribers that may have been created before getBars completed
        // This fixes the race condition where subscribeBars() was called first
        if (lastBar) {
          for (const [, sub] of this.subscribers.entries()) {
            const subNormalizedRes = normalizeResolution(sub.resolution);
            const subKey = `${symbol}_${subNormalizedRes}`;
            if (subKey === key) {
              // Only update if subscriber has no lastBar or this bar is more recent
              if (sub.lastBar === null || lastBar.time > sub.lastBar.time) {
                sub.lastBar = lastBar;
              }
            }
          }
        }

        onResult(bars, { noData: false });
      })
      .catch((error) => {
        console.error("[BackendDatafeed] Error fetching bars:", error);
        onResult([], { noData: true });
      });
  }

  subscribeBars(
    symbolInfo: TradingView.LibrarySymbolInfo,
    resolution: TradingView.ResolutionString,
    onTick: TradingView.SubscribeBarsCallback,
    listenerGuid: string
  ): void {
    const symbol = symbolInfo.name;

    // Initialize subscription tracking for this symbol
    if (!this.symbolSubscriptions.has(symbol)) {
      this.symbolSubscriptions.set(symbol, new Set());
    }
    this.symbolSubscriptions.get(symbol)!.add(listenerGuid);

    // Check if we need to create a WebSocket subscription for this symbol
    const needsNewSubscription = this.symbolSubscriptions.get(symbol)!.size === 1;

    // Initialize lastBar from historical data for seamless continuity
    // Use normalized resolution for consistent key matching with getBars
    const normalizedRes = normalizeResolution(resolution);
    const key = `${symbol}_${normalizedRes}`;
    const lastHistorical = this.lastHistoricalBars.get(key) || null;

    // Store subscriber data
    const subscriberData: SubscriberData = {
      callback: onTick,
      resolution: resolution,
      lastBar: lastHistorical,
    };

    if (needsNewSubscription) {
      // Subscribe to mark price WebSocket for real-time updates
      const unsubscribe = wsClient.subscribe(`price/${symbol}`, (message: WsMessage) => {
        if (message.type !== "price") return;

        const priceMsg = message as WsPriceMessage;
        const price = parseFloat(priceMsg.price);
        const timestamp = priceMsg.timestamp;

        if (isNaN(price) || price <= 0) return;

        // Update all subscribers for this symbol
        const guids = this.symbolSubscriptions.get(symbol);
        if (!guids) return;

        for (const guid of guids) {
          const sub = this.subscribers.get(guid);
          if (!sub) continue;

          // Check if historical data has loaded for this resolution
          const normalizedSubRes = normalizeResolution(sub.resolution);
          const historyKey = `${symbol}_${normalizedSubRes}`;
          const historyLoaded = this.historicalDataLoaded.has(historyKey);

          const lastBar = sub.lastBar;

          // Determine bar time based on resolution
          let barTime: number;
          if (sub.resolution === "1M" || sub.resolution === "M") {
            const date = new Date(timestamp);
            date.setUTCDate(1);
            date.setUTCHours(0, 0, 0, 0);
            barTime = date.getTime();
          } else if (sub.resolution === "1W" || sub.resolution === "W") {
            const date = new Date(timestamp);
            const day = date.getUTCDay();
            const diff = day === 0 ? -6 : 1 - day;
            date.setUTCDate(date.getUTCDate() + diff);
            date.setUTCHours(0, 0, 0, 0);
            barTime = date.getTime();
          } else if (sub.resolution === "1D" || sub.resolution === "D") {
            const date = new Date(timestamp);
            date.setUTCHours(0, 0, 0, 0);
            barTime = date.getTime();
          } else {
            const resolutionMinutes = parseInt(sub.resolution) || 1;
            const intervalMs = resolutionMinutes * 60 * 1000;
            barTime = Math.floor(timestamp / intervalMs) * intervalMs;
          }

          if (!lastBar || lastBar.time !== barTime) {
            // New bar needed - but only create if we have historical context
            if (!lastBar && !historyLoaded) {
              // Skip - historical data hasn't loaded yet
              continue;
            }
            
            // Use previous bar's close as open for visual continuity
            const openPrice = lastBar ? lastBar.close : price;
            
            const newBar: TradingView.Bar = {
              time: barTime,
              open: openPrice,
              high: Math.max(openPrice, price),
              low: Math.min(openPrice, price),
              close: price,
              volume: 0,
            };
            sub.lastBar = newBar;
            sub.callback(newBar);
          } else {
            // Update existing bar
            const updatedBar: TradingView.Bar = {
              ...lastBar,
              high: Math.max(lastBar.high, price),
              low: Math.min(lastBar.low, price),
              close: price,
            };
            sub.lastBar = updatedBar;
            sub.callback(updatedBar);
          }
        }
      });

      // Store unsubscribe keyed by symbol (not on individual subscriber)
      // This ensures cleanup works regardless of which subscriber is removed last
      this.wsUnsubscribers.set(symbol, unsubscribe);
    }

    this.subscribers.set(listenerGuid, subscriberData);
  }

  unsubscribeBars(listenerGuid: string): void {
    const subscriber = this.subscribers.get(listenerGuid);
    if (!subscriber) return;

    this.subscribers.delete(listenerGuid);

    // Find which symbol this guid belongs to
    for (const [symbol, guids] of this.symbolSubscriptions.entries()) {
      if (guids.has(listenerGuid)) {
        guids.delete(listenerGuid);

        // If no more subscribers for this symbol, clean up everything
        if (guids.size === 0) {
          // Unsubscribe WebSocket using the symbol-level unsubscriber
          const wsUnsub = this.wsUnsubscribers.get(symbol);
          if (wsUnsub) {
            wsUnsub();
            this.wsUnsubscribers.delete(symbol);
          }
          this.symbolSubscriptions.delete(symbol);
          
          // Clean up stale historical data for this symbol
          for (const key of this.lastHistoricalBars.keys()) {
            if (key.startsWith(`${symbol}_`)) {
              this.lastHistoricalBars.delete(key);
              this.historicalDataLoaded.delete(key);
            }
          }
        }
        break;
      }
    }
  }
}

// Export singleton instance
export const backendDatafeed = new BackendDatafeed();
